/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useEffect } from 'react';
import CheckCircle from '@material-ui/icons/CheckCircle';
import CancelOutlined from '@material-ui/icons/CancelOutlined';
import { Typography } from '@material-ui/core';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { merge } from 'lodash';

import WDT_STUDENT_PROGRESS_QUERY from 'graph/reports/wdt-student-progress-report-query';
import useReportQueryParams from '../../../../hooks/use-report-query-params';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import LwtTable, { ColumnProps } from 'components/@common/Table/LwtTable';
import { WdtCharacterProgressData } from 'types/schema-generated-types';
import { ModuleType } from '../../../../types';
import { TFunction } from 'i18next';
import { green } from '@material-ui/core/colors';
import { getQueryVariables } from '../../../../utils/report-param-utils';

const CompleteIcon: React.FC = () => <CheckCircle style={{ fontSize: 16, color: green[600] }} />;
const InCompleteIcon: React.FC = () => <CancelOutlined style={{ fontSize: 16 }} color="primary" />;

const getColumns: (t: TFunction) => ColumnProps<any>[] = t => [
  {
    id: 'character',
    label: t('Character'),
    sortKey: 'character.character',
    getValue: item => <Typography>{item.character?.character}</Typography>,
    sortable: true
  },
  {
    id: 'completed1Star',
    label: t('Completed 1 Star'),
    getValue: item => (item.level >= 1 ? <CompleteIcon /> : <InCompleteIcon />),
    sortable: false
  },
  {
    id: 'completed2Star',
    label: t('Completed 2 Star'),
    getValue: item => (item.level >= 2 ? <CompleteIcon /> : <InCompleteIcon />),
    sortable: false
  },
  {
    id: 'completed3Star',
    label: t('Completed 3 Star'),
    getValue: item => (item.level >= 3 ? <CompleteIcon /> : <InCompleteIcon />),
    sortable: false
  }
];

interface ProgressForCharactersTableViewProps {
  module: ModuleType;
}

const ProgressForCharactersTableView: React.FC<ProgressForCharactersTableViewProps> = ({
  module
}) => {
  const [{ dateRange, entityType, entityId }] = useReportQueryParams();
  const [fetchReport, { data, loading }] = useLazyQuery(WDT_STUDENT_PROGRESS_QUERY);
  const { t } = useTranslation('Reports');

  const columns = getColumns(t);

  const reportData: WdtCharacterProgressData[] =
    data?.wdtStudentProgressReport?.characterProgress ?? [];

  useEffect(() => {
    fetchReport({
      variables: merge(getQueryVariables({ entityId, entityType, dateRange }), {
        where: {
          module: module,
          characterOrder: 'natural'
        }
      })
    });
  }, [fetchReport, entityId, entityType, dateRange, module]);

  return (
    <ReportCard
      title={t(`Progress for ${t(module, { ns: 'ModuleType' })}`)}
      loading={loading}
      reportData={reportData}
    >
      <LwtTable columns={columns} data={reportData} />
    </ReportCard>
  );
};

export default ProgressForCharactersTableView;
